// SlicerPanel.jsx
// A dashboard panel whose sole job is to broadcast a filter to the
// dashboardFilterBus. Types:
//   - dropdown  → single-select dropdown of distinct column values
//   - multi     → multi-select checkbox list of distinct column values
//   - daterange → two date pickers (from/to); emits BETWEEN
//   - search    → free-text input; emits LIKE '%value%'
//
// Config shape (stored on the panel):
//   {
//     id, type: 'slicer', title,
//     x, y, w, h,
//     dbEngine: 'questdb' | 'mysql',
//     mysqlDatabase: string | null,
//     table: string,
//     column: string,
//     widgetType: 'dropdown' | 'multi' | 'daterange' | 'search',
//     operator: '=' | 'LIKE' | 'BETWEEN'  (derived from widgetType)
//   }

import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Filter, Search, Settings, Trash2, X, Copy } from 'lucide-react';
import reportService from '../../services/reportService';
import { useCrossFilters } from '../../services/dashboardFilterBus.jsx';

export default function SlicerPanel({
  config, plantId, darkMode = true, readOnly = false,
  onEdit, onDelete, onDuplicate
}) {
  const { emit, clearOne } = useCrossFilters();
  const slicerId = config.id;

  const theme = darkMode ? {
    card: '#1a1f35', bg: '#0a0e1a', text: '#f8fafc', mute: '#94a3b8',
    border: '#2d3548', accent: '#8b5cf6', chip: 'rgba(139,92,246,0.18)'
  } : {
    card: '#ffffff', bg: '#f8fafc', text: '#0f172a', mute: '#64748b',
    border: '#e2e8f0', accent: '#7c3aed', chip: 'rgba(124,58,237,0.12)'
  };

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  // Selected value(s) — kept per widget:
  //   dropdown  → single string
  //   multi     → Set<string>
  //   daterange → { from, to }
  //   search    → string
  const [selected, setSelected] = useState(
    config.widgetType === 'multi' ? new Set() :
    config.widgetType === 'daterange' ? { from: '', to: '' } : ''
  );

  const needsDistinct = config.widgetType === 'dropdown' || config.widgetType === 'multi';

  // Fetch distinct values for dropdown/multi widgets.
  useEffect(() => {
    if (!needsDistinct) return;
    if (!config.table || !config.column) return;
    setLoading(true);
    setError(null);
    const dbOpts = { dbEngine: config.dbEngine, mysqlDatabase: config.mysqlDatabase };
    reportService.getDistinctValues(plantId || '_', config.table, config.column, dbOpts)
      .then(vals => {
        // vals is either an array of primitives or of {value} — normalize
        const arr = (vals || []).map(v => (typeof v === 'object' && v !== null) ? Object.values(v)[0] : v)
          .filter(v => v !== null && v !== undefined && String(v).length > 0);
        setOptions(arr);
      })
      .catch(err => setError(err?.message || 'Failed to load values'))
      .finally(() => setLoading(false));
  }, [needsDistinct, plantId, config.dbEngine, config.mysqlDatabase, config.table, config.column]);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(o => String(o).toLowerCase().includes(q));
  }, [options, search]);

  // Debounce search input emission so we don't refetch on every keystroke
  useEffect(() => {
    if (config.widgetType !== 'search') return;
    const t = setTimeout(() => {
      if (!selected || String(selected).trim() === '') {
        clearOne(slicerId);
      } else {
        // LIKE '%value%'
        emit(slicerId, config.column, `%${String(selected).replace(/%/g, '\\%')}%`, 'LIKE');
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, config.widgetType, config.column]);

  // ── Selection handlers per widget ────────────────────────────────────────
  const pickDropdown = (val) => {
    if (val === selected) {
      setSelected('');
      clearOne(slicerId);
    } else {
      setSelected(val);
      emit(slicerId, config.column, val, '=');
    }
  };

  const toggleMulti = (val) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val); else next.add(val);
    setSelected(next);
    if (next.size === 0) {
      clearOne(slicerId);
    } else {
      // Emit as an IN filter — the bus doesn't parse operators; QuestDBPanel
      // interprets 'IN' with a comma-joined value list. Use a simpler
      // convention: emit each value with the same column via a JSON blob so
      // downstream can splat it. Simplest working approach: emit the whole
      // set joined by comma and use operator 'IN'.
      emit(slicerId, config.column, [...next].join(','), 'IN');
    }
  };

  const setRange = (patch) => {
    const nextRange = { ...selected, ...patch };
    setSelected(nextRange);
    if (nextRange.from && nextRange.to) {
      emit(slicerId, config.column, `${nextRange.from},${nextRange.to}`, 'BETWEEN');
    } else {
      clearOne(slicerId);
    }
  };

  const clearAll = () => {
    if (config.widgetType === 'multi') setSelected(new Set());
    else if (config.widgetType === 'daterange') setSelected({ from: '', to: '' });
    else setSelected('');
    clearOne(slicerId);
  };

  const isConfigured = !!(config.table && config.column && config.widgetType);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{
      width: '100%', height: '100%',
      background: theme.card, border: `1px solid ${theme.border}`,
      borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column',
      overflow: 'hidden', position: 'relative'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        marginBottom: 8, minHeight: 22
      }}>
        <Filter size={13} color={theme.accent} style={{ flexShrink: 0 }} />
        <div className="panel-drag-handle" style={{
          fontWeight: 700, fontSize: 12, color: theme.text,
          flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap', cursor: readOnly ? 'default' : 'move'
        }}>
          {config.title || 'Slicer'}
        </div>
        {!readOnly && (
          <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
            {onEdit && (
              <button onClick={() => onEdit(config)} title="Configure"
                style={btnStyle(theme)}>
                <Settings size={11} />
              </button>
            )}
            {onDuplicate && (
              <button onClick={() => onDuplicate(config)} title="Duplicate"
                style={btnStyle(theme)}>
                <Copy size={11} />
              </button>
            )}
            {onDelete && (
              <button onClick={() => onDelete(config.id)} title="Delete"
                style={{ ...btnStyle(theme), color: '#f87171' }}>
                <Trash2 size={11} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Empty state when not yet configured */}
      {!isConfigured && (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 12, textAlign: 'center', color: theme.mute, fontSize: 11
        }}>
          {readOnly
            ? 'Slicer not configured.'
            : 'Click the gear icon to pick a database, table, and column.'}
        </div>
      )}

      {/* Column label + clear */}
      {isConfigured && (
        <>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 6
          }}>
            <span style={{ fontSize: 10, color: theme.mute, textTransform: 'uppercase', fontWeight: 600 }}>
              {config.column}
            </span>
            {hasSelection(selected, config.widgetType) && (
              <button onClick={clearAll} title="Clear filter" style={{
                background: 'transparent', border: 'none', color: theme.accent,
                fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2
              }}>
                <X size={10} /> Clear
              </button>
            )}
          </div>

          {error && (
            <div style={{ fontSize: 10, color: '#f87171', marginBottom: 6 }}>{error}</div>
          )}

          {/* Widget bodies */}
          {config.widgetType === 'dropdown' && (
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <select
                value={selected || ''}
                onChange={e => pickDropdown(e.target.value)}
                disabled={loading}
                style={{
                  width: '100%', padding: '7px 10px', paddingRight: 26,
                  background: theme.bg, color: theme.text,
                  border: `1px solid ${theme.border}`, borderRadius: 6,
                  fontSize: 12, appearance: 'none', cursor: 'pointer'
                }}
              >
                <option value="">— {loading ? 'loading…' : 'all —'}</option>
                {options.map(o => (
                  <option key={String(o)} value={String(o)}>{String(o)}</option>
                ))}
              </select>
              <ChevronDown size={12} style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                color: theme.mute, pointerEvents: 'none'
              }} />
            </div>
          )}

          {config.widgetType === 'multi' && (
            <>
              <div style={{ position: 'relative', marginBottom: 6, flexShrink: 0 }}>
                <Search size={11} style={{
                  position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)',
                  color: theme.mute
                }} />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search values…"
                  style={{
                    width: '100%', padding: '6px 8px 6px 24px',
                    background: theme.bg, color: theme.text,
                    border: `1px solid ${theme.border}`, borderRadius: 6, fontSize: 11
                  }}
                />
              </div>
              <div style={{
                flex: 1, minHeight: 0, overflow: 'auto',
                display: 'flex', flexDirection: 'column', gap: 2
              }}>
                {loading && <div style={{ fontSize: 11, color: theme.mute }}>Loading…</div>}
                {!loading && filteredOptions.length === 0 && (
                  <div style={{ fontSize: 11, color: theme.mute, textAlign: 'center', padding: 8 }}>
                    No matching values
                  </div>
                )}
                {filteredOptions.map(o => {
                  const key = String(o);
                  const checked = selected.has(key);
                  return (
                    <label key={key} style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '4px 6px', borderRadius: 4, cursor: 'pointer',
                      background: checked ? theme.chip : 'transparent',
                      color: theme.text, fontSize: 11
                    }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMulti(key)}
                        style={{ accentColor: theme.accent }}
                      />
                      <span style={{
                        flex: 1, minWidth: 0, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>{key}</span>
                    </label>
                  );
                })}
              </div>
            </>
          )}

          {config.widgetType === 'daterange' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 10, color: theme.mute }}>
                From
                <input type="date"
                  value={selected.from || ''}
                  onChange={e => setRange({ from: e.target.value })}
                  style={dateInp(theme)} />
              </label>
              <label style={{ fontSize: 10, color: theme.mute }}>
                To
                <input type="date"
                  value={selected.to || ''}
                  onChange={e => setRange({ to: e.target.value })}
                  style={dateInp(theme)} />
              </label>
            </div>
          )}

          {config.widgetType === 'search' && (
            <div style={{ position: 'relative' }}>
              <Search size={12} style={{
                position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                color: theme.mute
              }} />
              <input
                value={selected}
                onChange={e => setSelected(e.target.value)}
                placeholder={`Search ${config.column}…`}
                style={{
                  width: '100%', padding: '7px 10px 7px 26px',
                  background: theme.bg, color: theme.text,
                  border: `1px solid ${theme.border}`, borderRadius: 6, fontSize: 12
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────
function hasSelection(sel, widget) {
  if (widget === 'multi') return sel instanceof Set && sel.size > 0;
  if (widget === 'daterange') return sel && sel.from && sel.to;
  return sel !== null && sel !== undefined && String(sel).length > 0;
}
function btnStyle(theme) {
  return {
    padding: 4, background: 'transparent', border: 'none',
    color: theme.mute, cursor: 'pointer', borderRadius: 4,
    display: 'flex', alignItems: 'center'
  };
}
function dateInp(theme) {
  return {
    display: 'block', width: '100%', marginTop: 3,
    padding: '5px 7px', background: theme.bg, color: theme.text,
    border: `1px solid ${theme.border}`, borderRadius: 6, fontSize: 11
  };
}
